import { css } from '@emotion/css';
import { TFunction } from 'i18next';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../features';
import { actions, BestFriendDeleteErrorType } from '../../features/bestFriends';
import { ConfirmButton } from '../shared/buttons/ConfirmButton';
import { NormalButton } from '../shared/buttons/NormalButton';
import { reveal } from '../shared/keyframe-animations';

const confirmBlockStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;

  .title,
  .desc,
  .confirm,
  .cancel {
    opacity: 0;
    visibility: hidden;
    transform: translateX(10px);
    animation: 0.2s ease-in-out ${reveal} both;
  }
  .title {
    font-size: 20px;
    text-align: center;
    color: #4d3906;
    animation-delay: 0.1s;
  }
  .desc {
    flex: 1 1 auto;
    padding: 12px 0 20px;
    animation-delay: 0.2s;
  }
  .confirm {
    width: 100%;
    margin: 4px 0 0;
    animation-delay: 0.3s;
  }
  .cancel {
    width: 100%;
    margin: 12px 0 4px;
    animation-delay: 0.4s;
  }
  .error {
    color: #e60012;
    font-size: 12px;
    text-align: center;
    margin: 16px auto;
  }
`;

interface ConfirmBlockProps {
  gaEventLabel: 'person' | 'report';
  description: string;
  confirm: string;
  onBlockClick: () => void;
  onCancelClick: () => void;
  title?: string;
  isLoading?: boolean;
}

export const ConfirmBlock: React.FC<ConfirmBlockProps> = ({
  title,
  description,
  confirm,
  gaEventLabel,
  onBlockClick,
  onCancelClick
}) => {
  const { t } = useTranslation(['best_friends', 'translation']);
  const history = useHistory();
  const dispatch = useDispatch();
  const { deleteStatus, deleteErrorType } = useSelector((state: RootState) => state.bestFriends);
  useEffect(() => {
    if (deleteStatus === 'succeeded') {
      history.replace(`/players/best-friends/block-succeeded?label=${gaEventLabel}`);
    } else if (deleteStatus === 'failed' && deleteErrorType === 'no-exist') {
      history.replace(`/players/best-friends/block-failed`);
    }
  }, [deleteErrorType, deleteStatus, gaEventLabel, history]);

  useEffect(() => {
    return () => {
      dispatch(actions.resetDeleteStatus());
    };
  }, [dispatch]);

  const isLoading = deleteStatus === 'loading';
  const error = getText(t, deleteErrorType);

  return (
    <section className={confirmBlockStyles}>
      <h1 className="title">{title}</h1>
      <p className="desc">{description}</p>
      {deleteErrorType && <p className="error">{error}</p>}
      <div className="confirm">
        <ConfirmButton background={'#E60012'} loading={isLoading} onClick={onBlockClick}>
          {confirm}
        </ConfirmButton>
      </div>
      <div className="cancel">
        <NormalButton onClick={onCancelClick} disabled={isLoading}>
          {t('translation:dialog_button_cancel')}
        </NormalButton>
      </div>
    </section>
  );
};

// helper function
function getText(t: TFunction, errorType?: BestFriendDeleteErrorType) {
  // doesn't handling 'no-exist' because page transitions.
  switch (errorType) {
    case 'unstable-network':
      return t('translation:error_unstable_network');
    case 'unknown':
    default:
      return t('translation:error_unknown');
  }
}
